/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import "@/App.css";
import { ArrowUpRight, Check, Clock3, Flame, Instagram, Menu, MessageCircle, ScanLine, X, Zap } from "lucide-react";

const WA_MAIN = process.env.REACT_APP_WHATSAPP_NUMBER;
const WA_HUB = process.env.REACT_APP_MACRO_HUB_NUMBER;
const wa = (num, text) => `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
const img = (id, w = 1000) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=85`;

const EASE = [0.22, 1, 0.36, 1];

const meals = [
  { type: "veg", name: "Chickpea Curry Bowl with Berries", kcal: 410, protein: 18, detail: "Slow-simmered chickpea curry over millet, finished with fresh berries and greens.", image: img("photo-1623428187969-5da2dcea5ebf", 800) },
  { type: "veg", name: "Paneer Curry-Rice Bowl with Green Salad", kcal: 520, protein: 26, detail: "Homestyle paneer curry with steamed rice and a crisp green salad.", image: img("photo-1588166524941-3bf61a9c41db", 800) },
  { type: "nonveg", name: "Grilled Chicken-Rice Bowl with Greek Yoghurt", kcal: 540, protein: 42, detail: "Herb-grilled chicken over fluffy rice, cooled with creamy Greek yoghurt.", image: img("photo-1579619002916-88cd4c81a70c", 800) },
  { type: "veg", name: "Sweet Corn-Tofu Green Salad", kcal: 360, protein: 21, detail: "Charred sweet corn, grilled tofu and crunchy greens with lime dressing.", image: img("photo-1546069901-ba9599a7e63c", 800) },
  { type: "veg", name: "Sweet Potato-Chickpea-Sprout Salad", kcal: 390, protein: 16, detail: "Roasted sweet potato, chickpeas and fresh sprouts with tahini drizzle.", image: img("photo-1631311695255-8dde6bf96cb5", 800) },
  { type: "veg", name: "Paneer-Quinoa Beetroot Salad with Edamame", kcal: 450, protein: 27, detail: "Paneer, quinoa and beetroot tossed with edamame seeds and citrus dressing.", image: img("photo-1719677775416-1dd6a93f1a73", 800) },
];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay, ease: EASE }}>{children}</motion.span>
  </span>
);

const Rise = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: EASE }}>{children}</motion.div>
);

const OrbitBadge = () => (
  <div className="orbit-badge" aria-hidden="true">
    <svg viewBox="0 0 120 120">
      <defs><path id="orbit-circle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" /></defs>
      <text><textPath href="#orbit-circle">FRESH DAYLIGHT NUTRITION • HIGH PROTEIN FUEL • </textPath></text>
    </svg>
    <span className="orbit-core"><Zap size={18} /></span>
  </div>
);

function App() {
  const [tab, setTab] = useState("all");
  const [mobileOpen, setMobileOpen] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yChipA = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const yChipB = useTransform(scrollYProgress, [0, 1], [0, -140]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); };
  }, []);

  const goTo = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    if (lenisRef.current) lenisRef.current.scrollTo(id, { offset: -70 });
    else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const visibleMeals = meals.filter((meal) => tab === "all" || meal.type === tab);
  const orderLink = (meal) => wa(WA_MAIN, `Hi Nutribowl! I want to order the ${meal.name} delivered to Noida/Greater Noida.`);

  return (
    <div className="site">
      <nav className="nav" data-testid="main-navigation">
        <div className="nav-inner">
          <a className="brand" href="#top" onClick={(e) => goTo(e, "#top")} data-testid="brand-home">
            <img src="/logo.webp" alt="Nutribowl logo" className="brand-logo" />
            <span className="brand-word">nutri<em>bowl</em><small>fresh daylight nutrition</small></span>
          </a>
          <div className="nav-links">
            <a href="#menu" onClick={(e) => goTo(e, "#menu")} data-testid="nav-menu-link">Menu</a>
            <a href="#science" onClick={(e) => goTo(e, "#science")} data-testid="nav-science-link">Why us</a>
            <a href="#subscriptions" onClick={(e) => goTo(e, "#subscriptions")} data-testid="nav-subscriptions-link">Plans</a>
            <a href="#macro-hub" onClick={(e) => goTo(e, "#macro-hub")} data-testid="nav-macro-link">NutriLens AI</a>
            <a className="btn btn-primary btn-sm" href={wa(WA_MAIN, "Hi Nutribowl! I'd like to place an order.")} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-order-button"><MessageCircle size={15} />Order now</a>
          </div>
          <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" data-testid="mobile-menu-button">{mobileOpen ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
        <AnimatePresence>
          {mobileOpen && (
            <motion.div className="mobile-menu" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
              <a href="#menu" onClick={(e) => goTo(e, "#menu")} data-testid="mobile-nav-menu-link">Menu</a>
              <a href="#science" onClick={(e) => goTo(e, "#science")} data-testid="mobile-nav-science-link">Why us</a>
              <a href="#subscriptions" onClick={(e) => goTo(e, "#subscriptions")} data-testid="mobile-nav-subscriptions-link">Plans</a>
              <a href="#macro-hub" onClick={(e) => goTo(e, "#macro-hub")} data-testid="mobile-nav-macro-link">NutriLens AI</a>
              <a className="btn btn-primary" href={wa(WA_MAIN, "Hi Nutribowl! I'd like to place an order.")} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-order-button"><MessageCircle size={15} />Order now</a>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main id="top">
        <section className="hero container" ref={heroRef} data-testid="hero-section">
          <div className="hero-copy">
            <motion.p className="eyebrow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}><span className="eyebrow-dot" />FRESH DAYLIGHT NUTRITION — NOIDA · GREATER NOIDA</motion.p>
            <h1>
              <MaskedLine delay={0.2}>Beat the</MaskedLine>
              <MaskedLine delay={0.32}><em>2 PM slump.</em></MaskedLine>
              <MaskedLine delay={0.44}>Eat like you mean it.</MaskedLine>
            </h1>
            <motion.p className="hero-text" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.62, ease: EASE }}>
              Macro-balanced bowls with 20–48g protein, built on digestive spices for all-day energy. Delivered to your desk from <strong>₹199.</strong>
            </motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.74, ease: EASE }}>
              <a className="btn btn-primary" href={wa(WA_MAIN, "Hi Nutribowl! I'd like to order a bowl.")} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"><MessageCircle size={17} />Order on WhatsApp<ArrowUpRight size={16} /></a>
              <a className="btn btn-ghost" href={wa(WA_HUB, "Hi NutriLens AI! Here is my meal. Please calculate my macros.")} target="_blank" rel="noopener noreferrer" data-testid="hero-nutrilens-cta"><ScanLine size={16} />Try NutriLens AI</a>
            </motion.div>
            <motion.div className="hero-proof" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.95 }}>
              <div><strong>4.9<span className="star">★</span></strong><small>loved by busy people</small></div>
              <div className="proof-line" />
              <div><strong>20–48g</strong><small>protein / bowl</small></div>
              <div className="proof-line" />
              <div><strong>₹199</strong><small>starting price</small></div>
            </motion.div>
          </div>

          <div className="hero-visual">
            <motion.div className="hero-arch" style={{ y: yImage }} initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ duration: 1.3, delay: 0.35, ease: EASE }}>
              <img src={img("photo-1546069901-ba9599a7e63c", 1200)} alt="Nutribowl signature high-protein bowl" data-testid="hero-bowl-image" />
            </motion.div>
            <motion.div className="chip chip-a" style={{ y: yChipA }} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 1.05, ease: EASE }} data-testid="hero-chip-protein">
              <span className="chip-icon"><Check size={14} /></span><span><b>48g</b><small>bio-available protein</small></span>
            </motion.div>
            <motion.div className="chip chip-b" style={{ y: yChipB }} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 1.2, ease: EASE }} data-testid="hero-chip-bloat">
              <span className="chip-icon mint"><Flame size={14} /></span><span><b>Zero bloat</b><small>carminative spices</small></span>
            </motion.div>
            <motion.div className="orbit-wrap" initial={{ opacity: 0, rotate: -30 }} animate={{ opacity: 1, rotate: 0 }} transition={{ duration: 1, delay: 1.3, ease: EASE }}><OrbitBadge /></motion.div>
          </div>
        </section>

        <div className="marquee" data-testid="editorial-marquee">
          <div className="marquee-track">
            {[0, 1].map((n) => (
              <span key={n} className="marquee-chunk">REAL FOOD <i>✳</i> REAL FUEL <i>✳</i> NO MIDDAY CRASH <i>✳</i> MACRO-BALANCED <i>✳</i> GUT-FRIENDLY <i>✳</i> MADE IN NOIDA <i>✳</i> </span>
            ))}
          </div>
        </div>

        <section className="section container" id="science" data-testid="science-section">
          <Rise className="section-head">
            <p className="eyebrow">THE NUTRIBOWL DIFFERENCE</p>
            <h2>Good food, <em>thought through.</em></h2>
            <p className="section-sub">We don't just count macros. We think about how food makes you feel at 2:00, 4:00 and 6:00 PM.</p>
          </Rise>
          <div className="bento">
            <Rise className="bento-card bento-wide" delay={0.05}>
              <span className="bento-num">01</span>
              <h3>20–48g bio-available protein</h3>
              <p>Seared paneer, edamame, herb-grilled chicken and slow-simmered chickpeas — portioned to actually hit your daily target.</p>
              <div className="macro-viz"><span style={{ "--w": "88%" }}><i>protein</i></span><span style={{ "--w": "52%" }}><i>complex carbs</i></span><span style={{ "--w": "34%" }}><i>healthy fats</i></span></div>
            </Rise>
            <Rise className="bento-card" delay={0.12}>
              <span className="bento-num">02</span>
              <h3>Digestive science. No bloat.</h3>
              <p>Hing, cumin, turmeric and coriander keep enzyme activity high and your gut calm through the workday.</p>
            </Rise>
            <Rise className="bento-card" delay={0.19}>
              <span className="bento-num">03</span>
              <h3>The 5-layer blueprint</h3>
              <p>Complex carbs, lean protein, fibre, healthy fats and a probiotic dressing. Every bowl, every time.</p>
            </Rise>
            <Rise className="bento-card bento-wide bento-terra" delay={0.26}>
              <span className="bento-num">04</span>
              <h3>Transparent &amp; affordable</h3>
              <p>Chef-prepared fitness nutrition from ₹199, delivered weekdays across Noida &amp; Greater Noida.</p>
              <span className="hours-chip"><Clock3 size={14} /> Weekdays · 11:30 AM – 1:30 PM &amp; 7:00 – 9:00 PM</span>
            </Rise>
          </div>
        </section>

        <section className="section menu-section" id="menu" data-testid="menu-section">
          <div className="container">
            <Rise className="section-head">
              <p className="eyebrow">THE GOOD STUFF</p>
              <h2>Pick your <em>power bowl.</em></h2>
              <p className="section-sub">Fresh, flavour-forward, and built to carry you through the day.</p>
            </Rise>
            <Rise className="tabs" delay={0.1}>
              {[["all", "All bowls"], ["veg", "Veg"], ["nonveg", "Non-veg"]].map(([key, label]) => (
                <button key={key} className={tab === key ? "tab active" : "tab"} onClick={() => setTab(key)} data-testid={`menu-tab-${key}`}>
                  {tab === key && <motion.span className="tab-pill" layoutId="tab-pill" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
                  {label}
                </button>
              ))}
            </Rise>
            <motion.div className="meal-grid" layout>
              <AnimatePresence mode="popLayout">
                {visibleMeals.map((meal) => (
                  <motion.article className="meal-card" key={meal.name} layout initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.94 }} transition={{ duration: 0.45, ease: EASE }} data-testid={`meal-card-${meal.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="meal-image">
                      <img src={meal.image} alt={meal.name} loading="lazy" />
                      <span className={meal.type === "nonveg" ? "tag tag-terra" : "tag"}>{meal.type === "nonveg" ? "NON-VEG" : "VEG"}</span>
                    </div>
                    <div className="meal-body">
                      <h3>{meal.name}</h3>
                      <p>{meal.detail}</p>
                      <div className="meal-foot">
                        <span className="meal-meta">{meal.kcal} kcal · {meal.protein}g protein</span>
                        <a className="btn btn-primary btn-sm" href={orderLink(meal)} target="_blank" rel="noopener noreferrer" data-testid={`order-${meal.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-button`}>Order<ArrowUpRight size={14} /></a>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>

        <section className="section macro-section" id="macro-hub" data-testid="macro-hub-section">
          <div className="container macro-layout">
            <Rise className="macro-copy">
              <p className="eyebrow">NUTRILENS — AI MACRO HUB</p>
              <h2>Snap. Send. <em>Track.</em></h2>
              <p className="section-sub">Send a photo or text of any meal to our WhatsApp assistant. NutriLens logs your calories, protein and fibre instantly — and keeps your daily totals without another app.</p>
              <a className="btn btn-primary" href={wa(WA_HUB, "Hi NutriLens AI! Here is my meal photo/text. Please calculate my macros.")} target="_blank" rel="noopener noreferrer" data-testid="nutrilens-whatsapp-cta"><MessageCircle size={17} />Chat with NutriLens<ArrowUpRight size={16} /></a>
              <p className="fine-print">No app download. No dashboard. Just WhatsApp.</p>
            </Rise>
            <Rise className="phone-wrap" delay={0.15}>
              <div className="phone-backdrop"><img src={img("photo-1544633909-3de7702b9602", 800)} alt="Fresh bowl photographed for macro tracking" loading="lazy" /></div>
              <div className="phone" data-testid="macro-chat-demo">
                <div className="chat-header">
                  <img src="/logo.webp" alt="NutriLens" className="chat-avatar" />
                  <span><b>NutriLens AI</b><small>online · replies instantly</small></span>
                  <MessageCircle size={18} />
                </div>
                <div className="chat-body">
                  <div className="chat-time">TODAY · 1:42 PM</div>
                  <motion.div className="bubble user-bubble" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
                    <img src={img("photo-1512621776951-a57141f2eefd", 500)} alt="Meal sent on WhatsApp" />
                    <span>lunch today</span>
                  </motion.div>
                  <motion.div className="bubble bot-bubble" initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.7 }}>
                    <span className="bot-label">NUTRILENS AI</span>
                    <b>Quinoa harvest bowl logged</b>
                    <code>~482 kcal{"\n"}P 28g · C 41g · F 19g{"\n"}Fibre 12g</code>
                    <span className="chat-total">Today: 1,286 kcal · 74g protein</span>
                    <span className="streak"><Flame size={13} /> 4-day streak. Keep going.</span>
                  </motion.div>
                </div>
                <div className="chat-input">Send a meal photo or text… <ScanLine size={15} /></div>
              </div>
            </Rise>
          </div>
        </section>

        <section className="section container fibre-section" data-testid="fiber-section">
          <Rise className="fibre-copy">
            <p className="eyebrow">WHAT'S INSIDE</p>
            <h2>Fibre that <em>works harder.</em></h2>
            <p className="section-sub">More than a number on a label — a colourful, intentional mix that keeps energy steady and your gut moving.</p>
            <a className="btn btn-ghost" href={wa(WA_MAIN, "Hi Nutribowl! What's inside my bowl today?")} target="_blank" rel="noopener noreferrer" data-testid="fibre-whatsapp-cta">Ask what's in your bowl<ArrowUpRight size={15} /></a>
          </Rise>
          <div className="fibre-bars">
            {[["Chia seeds", "34.4g", 1], ["Flaxseeds / grains", "27.3g", 0.8], ["Oats", "10.1g", 0.44], ["Chickpeas", "7.6g", 0.3], ["Fresh fruit bowl", "3.3g", 0.19]].map(([name, amount, ratio], i) => (
              <Rise className="fibre-row" key={name} delay={i * 0.08}>
                <div className="fibre-label"><span>{name}</span><b>{amount} <small>fibre / 100g</small></b></div>
                <div className="bar-track">
                  <motion.span initial={{ width: 0 }} whileInView={{ width: `${ratio * 100}%` }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.2 + i * 0.08, ease: EASE }} />
                </div>
              </Rise>
            ))}
          </div>
        </section>

        <section className="section container subs-section" id="subscriptions" data-testid="subscriptions-section">
          <Rise className="section-head">
            <p className="eyebrow">MAKE IT A HABIT</p>
            <h2>Small choice. <em>Big momentum.</em></h2>
            <p className="section-sub">Two fresh bowls a day — lunch and dinner, sorted. Pick a pass and we handle the rest.</p>
          </Rise>
          <div className="price-grid">
            <Rise className="price-card" delay={0.08}>
              <p className="price-kicker">3-DAY TRIAL PASS</p>
              <h3>₹1,199 <small>/ 3 days</small></h3>
              <span className="price-note">Taste the routine before you commit.</span>
              <ul>
                <li><Check size={15} />2 bowls a day — lunch + dinner</li>
                <li><Check size={15} />Any bowl from the full menu</li>
                <li><Check size={15} />Office or society-gate delivery</li>
              </ul>
              <a className="btn btn-primary" href={wa(WA_MAIN, "Hi Nutribowl! I want to start the 3-Day Trial Pass (₹1,199, 2 meals/day).")} target="_blank" rel="noopener noreferrer" data-testid="trial-plan-cta">Start my trial<ArrowUpRight size={15} /></a>
            </Rise>
            <Rise className="price-card price-pop" delay={0.16}>
              <span className="price-flag">MOST POPULAR</span>
              <p className="price-kicker">5-DAY EXECUTIVE PASS</p>
              <h3>₹1,899 <small>/ 5 days</small></h3>
              <span className="price-note">Your full workweek, fully fuelled.</span>
              <ul>
                <li><Check size={15} />2 bowls a day — lunch + dinner</li>
                <li><Check size={15} />Rotate across the full menu</li>
                <li><Check size={15} />Priority delivery slots</li>
              </ul>
              <a className="btn btn-terra" href={wa(WA_MAIN, "Hi Nutribowl! I want the 5-Day Executive Pass (₹1,899, 2 meals/day).")} target="_blank" rel="noopener noreferrer" data-testid="executive-plan-cta">Get the executive pass<ArrowUpRight size={15} /></a>
            </Rise>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-grid">
          <div>
            <a className="brand" href="#top" onClick={(e) => goTo(e, "#top")} data-testid="footer-brand">
              <img src="/logo.webp" alt="Nutribowl logo" className="brand-logo" />
              <span className="brand-word">nutri<em>bowl</em><small>fresh daylight nutrition</small></span>
            </a>
            <p className="footer-tag">Productivity fuel for people who have places to be.</p>
          </div>
          <div>
            <p className="footer-label">FIND US</p>
            <a href={wa(WA_MAIN, "Hi Nutribowl!")} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link">+91 84397 21669</a>
            <a href="https://instagram.com/gonutribowl" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link"><Instagram size={14} /> @gonutribowl</a>
          </div>
          <div>
            <p className="footer-label">SERVICE AREA</p>
            <p data-testid="service-area">Noida &amp; Greater Noida</p>
            <p>Daily · 8:00 AM – 10:00 PM</p>
          </div>
        </div>
        <div className="container footer-bottom">
          <span>© 2026 Nutribowl. Made for better days.</span>
          <span>www.gonutribowl.in</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
